/**
 * Lead Create Component
 *
 * Minimal creation form — a lead needs at least a name or an email.
 * The rest of the contact information is filled in from the edit page.
 */

import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  useTranslate,
  maxLength,
  email,
  TopToolbar,
  ListButton,
} from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { PersonAddRounded as CreateIcon } from '@mui/icons-material';
import { MyToolbar } from '@/components/forms';
import { HelpTooltip } from '@/components/common/HelpTooltip';

/**
 * Create page actions toolbar
 */
const CreateActions: React.FC = () => {
  const translate = useTranslate();

  return (
    <TopToolbar sx={{ justifyContent: 'space-between', alignItems: 'center', minHeight: '64px', px: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontSize: '1.75rem' }}>
          <CreateIcon fontSize="inherit" />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {translate('resources.leads.create.title')}
        </Typography>
        <HelpTooltip title={translate('resources.leads.help.createBanner')} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <ListButton />
      </Box>
    </TopToolbar>
  );
};

/**
 * Lead Create Component
 */
export const LeadCreate = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Create redirect="edit" actions={<CreateActions />}>
      <SimpleForm
        toolbar={<MyToolbar resource="leads" />}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: isMobile ? 10 : 0,
        }}
      >
        <Box sx={{ maxWidth: 'md', mx: 'auto', width: '100%', px: { xs: 1, sm: 2 } }}>
          <Card sx={{ border: 1, borderColor: 'divider', mb: 4 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  {translate('resources.leads.sections.basicInfo')}
                </Typography>
                <HelpTooltip title={translate('resources.leads.help.createBanner')} />
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="firstName"
                    label={translate('resources.leads.fields.firstName')}
                    validate={maxLength(200)}
                    fullWidth
                    helperText={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="lastName"
                    label={translate('resources.leads.fields.lastName')}
                    validate={maxLength(200)}
                    fullWidth
                    helperText={false}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    source="email"
                    label={translate('resources.leads.fields.email')}
                    validate={email()}
                    fullWidth
                    helperText={false}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </SimpleForm>
    </Create>
  );
};

export default LeadCreate;
